import { userProfileService } from '../userProfileService';
import { buildDisplayFromParts, normalizeCountryCode } from '../../utils/location';
import { geocodingClient } from './geocodingClient';

const resolveCoordinateValue = (preferredValue, fallbackValue) => {
  if (typeof preferredValue === 'number' && Number.isFinite(preferredValue)) {
    return preferredValue;
  }
  if (typeof fallbackValue === 'number' && Number.isFinite(fallbackValue)) {
    return fallbackValue;
  }
  return null;
};

export function toResolvedGeocodedLocation(resolvedLocation, fallbackCoordinates = {}) {
  if (!resolvedLocation || typeof resolvedLocation !== 'object') {
    return null;
  }

  const label = typeof resolvedLocation.formatted === 'string' && resolvedLocation.formatted.trim().length > 0
    ? resolvedLocation.formatted.trim()
    : buildDisplayFromParts({
      city: resolvedLocation.city,
      stateOrRegion: resolvedLocation.stateOrRegion,
      country: resolvedLocation.country,
    });

  return {
    label,
    fullAddress: label,
    addressLine: typeof resolvedLocation.addressLine === 'string' && resolvedLocation.addressLine.trim().length > 0
      ? resolvedLocation.addressLine.trim()
      : null,
    countryName: resolvedLocation.country || null,
    countryCode: normalizeCountryCode(resolvedLocation.countryCode || '') || null,
    regionName: resolvedLocation.stateOrRegion || null,
    cityName: resolvedLocation.city || null,
    latitude: resolveCoordinateValue(fallbackCoordinates.latitude, resolvedLocation.latitude),
    longitude: resolveCoordinateValue(fallbackCoordinates.longitude, resolvedLocation.longitude),
  };
}

export async function resolveLocationFromCoordinates({ latitude, longitude, locale }) {
  let reverseLookupError = null;

  try {
    const reverseResult = await geocodingClient.reverseLookup({
      lat: latitude,
      lon: longitude,
      limit: 1,
    });
    if (reverseResult) {
      return {
        location: {
          ...reverseResult,
          latitude: resolveCoordinateValue(latitude, reverseResult.latitude),
          longitude: resolveCoordinateValue(longitude, reverseResult.longitude),
        },
        failed: false,
      };
    }
  } catch (err) {
    reverseLookupError = err;
  }

  try {
    const resolved = await userProfileService.resolveLocationByCoordinates({
      latitude,
      longitude,
      locale,
    });
    const fallbackLocation = toResolvedGeocodedLocation(resolved?.resolved ? resolved.location : null, {
      latitude,
      longitude,
    });
    if (fallbackLocation) {
      return {
        location: fallbackLocation,
        failed: false,
      };
    }
  } catch (fallbackError) {
    if (!reverseLookupError) {
      reverseLookupError = fallbackError;
    }
  }

  return {
    location: null,
    failed: Boolean(reverseLookupError),
    error: reverseLookupError,
  };
}
